import { Link, useLocation } from "react-router-dom";
import { Heart, Star, Users, User, Activity, Shield, ListChecks } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useIsAdmin } from "@/hooks/useIsAdmin";


const iconMap = {
  Heart,
  Star,
  Users,
  User,
  Activity,
  Shield,
  ListChecks,
};

interface DesktopNavLinksProps {
  isAuthenticated: boolean;
}

export const DesktopNavLinks = ({ isAuthenticated }: DesktopNavLinksProps) => {
  const location = useLocation();
  const { data: isAdmin } = useIsAdmin();
  
  
  const { data: navigationItems } = useQuery({
    queryKey: ['navigation-items'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('navigation_items')
        .select('*')
        .not('title', 'eq', 'My Profile')
        .order('created_at', { ascending: true });
      
      if (error) throw error;
      return data;
    },
  });

  const { data: profile } = useQuery({
    queryKey: ['profile'],
    enabled: isAuthenticated,
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');
      
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();
      
      if (error) throw error;
      return data;
    },
  });

  // Hide member-only items from guests; hide "Home Base" once signed in (its content is the / route already).
  const memberOnlyTitles = ['My Profile', 'Messages', 'Bookmarks', 'Home Base'];
  const filteredItems = navigationItems?.filter(item => {
    if (item.title === 'Home Base') return false;
    if (!isAuthenticated && memberOnlyTitles.includes(item.title)) return false;
    return true;
  }) || [];


  return (
    <div className="hidden lg:flex items-center gap-1">
      {filteredItems?.map((item) => {
        const isActive = location.pathname === item.url;
        return (
          <Link
            key={item.id}
            to={item.url}
            className={`relative px-3 py-2 font-display text-xs font-bold uppercase tracking-widest transition-colors hover:text-accent ${
              isActive ? 'text-foreground' : 'text-foreground/70'
            }`}
          >
            <span>{item.title}</span>
            {isActive && (
              <span className="absolute left-3 right-3 -bottom-0.5 h-0.5 bg-accent" aria-hidden />
            )}
          </Link>
        );
      })}
      
      {isAuthenticated && isAdmin && (
        <Link
          to="/admin"
          className={`relative px-3 py-2 font-display text-xs font-bold uppercase tracking-widest transition-colors hover:text-accent ${
            location.pathname === '/admin' ? 'text-foreground' : 'text-foreground/70'
          }`}
        >
          <span>Admin</span>
          {location.pathname === '/admin' && (
            <span className="absolute left-3 right-3 -bottom-0.5 h-0.5 bg-accent" aria-hidden />
          )}
        </Link>
      )}
    </div>
  );
};